import { Component, OnInit, signal } from '@angular/core';
import {
  ControlContainer,
  FormBuilder,
  FormGroup,
  FormGroupDirective,
  Validators,
} from '@angular/forms';
import { Entity } from 'src/app/shared/store/view/types';
import { DataType, DropdownStyle } from 'src/app/shared/store/typeahead/types';
import { ContractService } from '../../../../../../shared/store/contract/contract.service';
import { Observable } from 'rxjs';
import { HouseInsuranceContractModel } from '../../../../../../shared/store/contract/types';
import { filter, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  removeValidators,
  setValidators,
} from '../../../../../../shared/helper/formhelper';

@UntilDestroy()
@Component({
  selector: 'app-contract-form-house',
  templateUrl: './contract-form-house.component.html',
  styleUrls: ['./contract-form-house.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  standalone: false,
})
export class ContractFormHouseComponent implements OnInit {
  houseDetail$: Observable<HouseInsuranceContractModel>;
  houseInsuranceContractModel$: Observable<HouseInsuranceContractModel>;
  form: FormGroup;

  isFlat = signal<boolean>(false);

  protected readonly Entity = Entity;
  protected readonly DropdownStyle = DropdownStyle;
  protected readonly DataType = DataType;

  constructor(
    private readonly contractService: ContractService,
    private parent: FormGroupDirective,
    private formBuilder: FormBuilder,
  ) {
    this.houseDetail$ = this.contractService.getHouseDetail.value$;
    this.houseInsuranceContractModel$ =
      this.contractService.getHouseContractModel.value$;
  }

  ngOnInit(): void {
    this.form = this.parent.form;

    this.form.addControl(
      'houseFormGroup',
      this.formBuilder.group({
        houseHousingType: [undefined, [Validators.required]],
        houseFlatLevel: [undefined],
        houseNbOfRooms: [1],
        houseAmountToInsure: [undefined, [Validators.required]],
        houseAddress: ['', [Validators.required]],
        houseZipCode: [
          '',
          [
            Validators.required,
            Validators.minLength(5),
            Validators.maxLength(5),
          ],
        ],
        houseCity: [undefined, [Validators.required]],
        houseResidencyType: [undefined, [Validators.required]],
        houseArea: [1, [Validators.required]],
        houseConstructionType: [undefined, [Validators.required]],
        houseRoofType: [undefined, [Validators.required]],
        houseOccupancyRole: [undefined],
        houseCurrentInsuranceCompany: [undefined],
        houseFireCoverage: [false],
        houseWaterCoverage: [false],
        houseTheftCoverage: [false],
        houseGlassBreakCoverage: [false],
        housePublicLiabilityCoverage: [false],
        houseFeeCancellationCoverage: [false],
        housePoolCoverage: [false],
        houseDogCoverage: [false],
        houseElectricalDamageCoverage: [false],
        profession: [''],
        maritalStatus: [''],
      }),
    );

    this.houseInsuranceContractModel$
      .pipe(
        filter((x) => !!x),
        take(1),
      )
      .subscribe((x) => {
        this.form.patchValue({
          houseFormGroup: {
            houseHousingType: x.housingType,
            houseNbOfRooms: x.nbOfRooms,
            houseAmountToInsure: x.amountToInsure,
            houseAddress: x.address,
            houseZipCode: x.zipCode,
            houseCity: x.city,
            houseFlatLevel: x.flatLevel,
            houseResidencyType: x.residencyType,
            houseArea: x.area,
            houseConstructionType: x.constructionType,
            houseRoofType: x.roofType,
            houseOccupancyRole: x.occupancyRole,
            houseCurrentInsuranceCompany: x.currentInsuranceCompany,
            houseFireCoverage: x.fireCoverage,
            houseWaterCoverage: x.waterCoverage,
            houseTheftCoverage: x.theftCoverage,
            houseGlassBreakCoverage: x.glassBreakCoverage,
            housePublicLiabilityCoverage: x.publicLiabilityCoverage,
            houseFeeCancellationCoverage: x.feeCancellationCoverage,
            housePoolCoverage: x.poolCoverage,
            houseDogCoverage: x.dogCoverage,
            houseElectricalDamageCoverage: x.electricalDamageCoverage,
            profession: x.profession,
            maritalStatus: x.maritalStatus,
          },
        });
      });

    this.houseDetail$
      .pipe(
        filter((x) => !!x),
        take(1),
      )
      .subscribe((x) => {
        this.form.patchValue({
          houseFormGroup: {
            houseHousingType: x.housingType,
            houseNbOfRooms: x.nbOfRooms,
            houseAmountToInsure: x.amountToInsure,
            houseAddress: x.address,
            houseZipCode: x.zipCode,
            houseCity: x.city,
            houseFlatLevel: x.flatLevel,
            houseResidencyType: x.residencyType,
            houseArea: x.area,
            houseConstructionType: x.constructionType,
            houseRoofType: x.roofType,
            houseOccupancyRole: x.occupancyRole,
            houseCurrentInsuranceCompany: x.currentInsuranceCompany,
            houseFireCoverage: x.fireCoverage,
            houseWaterCoverage: x.waterCoverage,
            houseTheftCoverage: x.theftCoverage,
            houseGlassBreakCoverage: x.glassBreakCoverage,
            housePublicLiabilityCoverage: x.publicLiabilityCoverage,
            houseFeeCancellationCoverage: x.feeCancellationCoverage,
            housePoolCoverage: x.poolCoverage,
            houseDogCoverage: x.dogCoverage,
            houseElectricalDamageCoverage: x.electricalDamageCoverage,
            profession: x.profession,
            maritalStatus: x.maritalStatus,
          },
        });
      });

    this.form.controls['houseFormGroup']
      .get('houseHousingType')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe((value) => {
        if (value === 'Appartement') {
          this.isFlat.set(true);

          setValidators(
            this.form.controls['houseFormGroup'].get('houseFlatLevel'),
            Validators.required,
          );
        } else {
          this.isFlat.set(false);

          this.form.controls['houseFormGroup']
            .get('houseFlatLevel')
            ?.setValue(undefined);

          removeValidators(
            this.form.controls['houseFormGroup'].get('houseFlatLevel'),
            Validators.required,
          );
        }
      });
  }
}
